import PropTypes from "prop-types";
// material-ui
import { useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";

// project-imports
import DrawerHeaderStyled from "./DrawerHeaderStyled";

import Logo from "components/logo";
import { HEADER_HEIGHT } from "config";
import { handlerDrawerOpen } from "api/menu";
import { V2 } from "../drawerV2Tokens";
import { X as CloseIcon } from "lucide-react";

/** Remix RiMenuFoldLine — reduce sidebar */
function MenuFoldIcon({ size = 22 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden
    >
      <path d="M21 17.9995V19.9995H3V17.9995H21ZM6.59619 3.90332L8.01041 5.31753L4.82843 8.49951L8.01041 11.6815L6.59619 13.0957L2 8.49951L6.59619 3.90332ZM21 10.9995V12.9995H12V10.9995H21ZM21 3.99951V5.99951H12V3.99951H21Z" />
    </svg>
  );
}

// ==============================|| DRAWER HEADER ||============================== //

export default function DrawerHeader({ open, fullWidthMobile = false, onClose }) {
  const theme = useTheme();

  const toggleBtn = open ? (
    <IconButton
      aria-label="Réduire le menu"
      onClick={() => handlerDrawerOpen(false)}
      disableRipple
      sx={{
        color: V2.ink,
        flexShrink: 0,
        p: 0,
        borderRadius: 0,
        bgcolor: "transparent",
        "&:hover": {
          bgcolor: "transparent",
          color: V2.accent,
        },
      }}
    >
      <MenuFoldIcon size={22} />
    </IconButton>
  ) : null;

  if (fullWidthMobile) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1.75,
          minHeight: HEADER_HEIGHT,
          width: "100%",
          bgcolor: V2.surfaceSolid,
          borderBottom: `1px solid ${V2.line}`,
          fontFamily: V2.fontBody,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            fontFamily: V2.fontBody,
            color: V2.ink,
            fontSize: "1.05rem",
          }}
        >
          Menu
        </Typography>
        <IconButton
          onClick={onClose}
          aria-label="Fermer le menu"
          size="large"
          sx={{
            color: V2.ink,
            borderRadius: "var(--brand-radius-md)",
            border: `1px solid ${V2.line}`,
            width: 40,
            height: 40,
            "&:hover": {
              bgcolor: V2.accentSoft,
              borderColor: V2.accentBorder,
              color: V2.accent,
            },
          }}
        >
          <CloseIcon size={20} />
        </IconButton>
      </Box>
    );
  }

  return (
    <DrawerHeaderStyled
      theme={theme}
      open={open}
      sx={{
        minHeight: open ? "auto" : HEADER_HEIGHT,
        width: "inherit",
        pt: open ? 1 : 2.5,
        pb: open ? "16px" : 2.5,
        px: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        gap: open ? 0.5 : 0,
      }}
    >
      {/* Fold control only when open — top-right, above logo.
          When collapsed, open control lives above Assistance. */}
      {open && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            px: 1.25,
            lineHeight: 0,
          }}
        >
          {toggleBtn}
        </Box>
      )}

      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          px: open ? 1.5 : 0,
          pt: open ? "20px" : 0,
          pb: open ? "20px" : 0,
        }}
      >
        <Logo
          isIcon={!open}
          height={open ? 56 : undefined}
          maxWidth={open ? 190 : undefined}
          sx={{ width: open ? "auto" : 59, height: "auto" }}
        />
      </Box>
    </DrawerHeaderStyled>
  );
}

DrawerHeader.propTypes = {
  open: PropTypes.bool,
  fullWidthMobile: PropTypes.bool,
  onClose: PropTypes.func,
};
